import type {MouseEvent} from 'react';
import {useIntl} from 'react-intl';
import noop from '@tinkoff/utils/function/noop';

import type {EventParametersMap} from '@time-webkit/statist';
import {AppsIcon} from '@time-webkit/all/icons/apps';
import {type PluginComponent} from 'types/store/plugins';
import {isReminderPlugin} from 'utils/isReminderPlugin';

import {type usePostMenuData} from './use_post_menu_data';
import {type PostMenuItem, type UsePostMenuItemsParams} from './types';

const mapPluginComponentToMenuItem = (
    pluginComponent: PluginComponent,
    postId: string,
    className: string | undefined,
    logAction: Parameters<typeof usePostMenuPluginItem>[2],
): PostMenuItem => {
    const hasSubmenu = (pluginComponent.subMenu || []).length > 0;
    return {
        attributes: {id: `plugins_post_${pluginComponent.id}`, 'data-qa': `post_plugin_${pluginComponent.pluginId}`},
        className,
        text: pluginComponent.text,
        shortcut: null,
        icon: null,
        preventClose: hasSubmenu,
        action: (event) => {
            pluginComponent.action?.(postId);

            if (!hasSubmenu && isReminderPlugin(pluginComponent)) {
                logAction('remind', event);
            }
        },
        subMenu: pluginComponent.subMenu?.map((item) => {
            return mapPluginComponentToMenuItem(
                item as PluginComponent,
                postId,
                isReminderPlugin(pluginComponent) ? 'ReminderPluginSubMenuItem' : undefined,
                logAction,
            );
        }),
    };
};

export function usePostMenuPluginItem(
    params: UsePostMenuItemsParams,
    {pluginItems}: ReturnType<typeof usePostMenuData>,
    logAction: (
        action: EventParametersMap['app.postAction']['action'],
        event: MouseEvent | KeyboardEvent
    ) => void,
): PostMenuItem | null {
    const {post} = params;
    const {formatMessage} = useIntl();

    if (!pluginItems?.length) {
        return null;
    }

    const isReminderOnly = pluginItems.length === 1 && isReminderPlugin(pluginItems[0]!);

    if (isReminderOnly) {
        return mapPluginComponentToMenuItem(pluginItems[0]!, post.id, undefined, logAction);
    }

    return {
        attributes: {id: `plugins_post_${post.id}`, 'data-qa': 'post_plugins'},
        text: formatMessage({id: 'post_info.message_actions', defaultMessage: 'More message actions'}),
        shortcut: null,
        icon: <AppsIcon width={18} height={18} />,
        action: noop,
        preventClose: true,
        subMenu: pluginItems.map((item) => mapPluginComponentToMenuItem(item, post.id, undefined, logAction)),
    };
}
